const { debounce } = window.BAO.Utils

const setInitialised = () => {
  /** @param {KeenSliderInstance} carousel */
  return carousel => {
    carousel.on('created', carousel =>
      carousel.container.setAttribute(`data-initialised`, true),
    )
  }
}

/**
 * @param {{
 *  next?: HTMLButtonElement,
 *  previous?: HTMLButtonElement,
 * }} arrows
 */
const useArrows = ({ next, previous } = {}) => {
  /** @param {KeenSliderInstance} carousel */
  return carousel => {
    next?.addEventListener('click', carousel.next)
    previous?.addEventListener('click', carousel.prev)

    const updateButtonStates = () => {
      const { abs, maxIdx, min } = carousel.track.details
      if (next) {
        next.disabled = abs === maxIdx
        next.setAttribute('aria-hidden', (maxIdx === 0).toString())
      }

      if (previous) {
        previous.disabled = abs === min
        previous.setAttribute('aria-hidden', (maxIdx === 0).toString())
      }
    }

    carousel.on('created', updateButtonStates)
    carousel.on('slideChanged', updateButtonStates)
    window.addEventListener('resize', updateButtonStates)
  }
}

/**
 * @param {number} seconds
 * @param {{
 *  pauseOnHover?: boolean
 * }} options
 */
const useAutoplay = (seconds = 2, { pauseOnHover = true } = {}) => {
  const delay = seconds * 1000

  /** @param {KeenSliderInstance} carousel */
  return carousel => {
    let timeout
    let mouseOver = false

    const clearCurrentTimout = () => clearTimeout(timeout)
    const setNextTimeout = () => {
      clearCurrentTimout()

      if (mouseOver) {
        return
      }

      timeout = setTimeout(carousel.next, delay)
    }

    carousel.on('created', () => {
      if (pauseOnHover) {
        carousel.container.addEventListener('mouseover', () => {
          mouseOver = true
          clearCurrentTimout()
        })
        carousel.container.addEventListener('mouseout', () => {
          mouseOver = false
          setNextTimeout()
        })
      }

      setNextTimeout()
    })

    carousel.on('dragStarted', clearCurrentTimout)
    carousel.on('animationEnded', setNextTimeout)
    carousel.on('updated', setNextTimeout)
  }
}

/** @param {HTMLButtonElement[]} dots */
const useDots = (dots = []) => {
  /** @param {KeenSliderInstance} carousel */
  return carousel => {
    const updateDotStates = () => {
      const { progress } = carousel.track.details
      const activeIndex = Number.isFinite(progress)
        ? Math.round(progress * (dots.length - 1))
        : 0

      dots.forEach((dot, index) => {
        dot.setAttribute('aria-current', String(index === activeIndex))
      })
    }

    const dotClickHandlers = dots.map((dot, index) => () =>
      carousel.moveToIdx(index),
    )

    dots.forEach((dot, index) => {
      dot.addEventListener('click', dotClickHandlers[index])
    })

    carousel.on('created', updateDotStates)
    carousel.on('detailsChanged', updateDotStates)

    carousel.on('destroyed', () => {
      dots.forEach((dot, index) => {
        dot.removeEventListener('click', dotClickHandlers[index])
      })
    })
  }
}

/** @param {number} idx */
const useResetIdxOnResize = (idx = 0) => {
  /** @param {KeenSliderInstance} carousel */
  return carousel => {
    window.addEventListener(
      'resize',
      debounce(() => carousel.moveToIdx(idx), 200),
    )
  }
}

/**
 * Adds wheel / trackpad controls to a Keen carousel by translating wheel input
 * into Keen's drag events.
 *
 * Options:
 * - sensitivity: drag distance multiplier. 1 is closest to native drag distance.
 * - maxDelta: caps large wheel spikes so the carousel does not jump too far.
 * - axisThreshold: how clearly the wheel gesture must match the carousel axis.
 * - dragEndDelay: delay after the last wheel event before releasing the drag.
 * - lineHeight: fallback multiplier for browsers that report wheel deltas in lines.
 * - invert: vertical scroll is converted to horizontal and vice versa.
 */
const addWheelControls = (
  direction,
  {
    sensitivity = 1,
    maxDelta = 40,
    axisThreshold = 1.2,
    dragEndDelay = 150,
    lineHeight = 16,
    invert = false,
  } = {},
) => {
  /** @param {KeenSliderInstance} carousel */
  return carousel => {
    let touchTimeout = null
    let wheelActive = false
    let position = {}

    if (carousel.options.disabled) return

    const isHorizontal = direction === 'horizontal'

    carousel?.container?.style?.setProperty(
      direction === 'horizontal'
        ? invert
          ? 'overscroll-behavior-y'
          : 'overscroll-behavior-x'
        : invert
          ? 'overscroll-behavior-x'
          : 'overscroll-behavior-y',
      'contain',
    )

    const clamp = (value, min, max) => {
      return Math.min(Math.max(value, min), max)
    }

    const normaliseDelta = event => {
      const multiplier =
        event.deltaMode === 1
          ? lineHeight
          : event.deltaMode === 2
            ? window.innerHeight
            : 1

      const rawDelta = isHorizontal
        ? invert
          ? event.deltaY
          : event.deltaX
        : invert
          ? event.deltaX
          : event.deltaY

      return clamp(rawDelta * multiplier, -maxDelta, maxDelta)
    }

    const hasCorrectScrollDirection = event => {
      const absX = Math.abs(event.deltaX)
      const absY = Math.abs(event.deltaY)

      return isHorizontal
        ? absX > absY * axisThreshold
        : absY > absX * axisThreshold
    }

    const dispatch = (event, name) => {
      const delta = normaliseDelta(event)

      if (isHorizontal) {
        position.x -= delta * sensitivity
      } else {
        position.y -= delta * sensitivity
      }

      carousel.container.dispatchEvent(
        new CustomEvent(name, {
          detail: {
            x: position.x,
            y: position.y,
          },
        }),
      )
    }

    const wheelStart = event => {
      position = {
        x: event.pageX,
        y: event.pageY,
      }

      carousel.container.dispatchEvent(
        new CustomEvent('ksDragStart', {
          detail: {
            x: position.x,
            y: position.y,
          },
        }),
      )
    }

    const wheel = event => {
      dispatch(event, 'ksDrag')
    }

    const wheelEnd = () => {
      carousel.container.dispatchEvent(
        new CustomEvent('ksDragEnd', {
          detail: {
            x: position.x,
            y: position.y,
          },
        }),
      )
    }

    const eventWheel = event => {
      let supportWheel = true
      let earlyReturn = false
      const { details } = carousel?.track

      if (!hasCorrectScrollDirection(event) && !invert) {
        supportWheel = false
        earlyReturn = true
      }

      /* Prevent overscrolling for none looping carousels */
      if (!earlyReturn && !carousel.options?.loop) {
        const scrollIndex = normaliseDelta(event)

        if (
          (details.progress <= 0 && scrollIndex < 0) ||
          (details.position >= details.max && scrollIndex > 0)
        ) {
          earlyReturn = true
        }
      }

      // If we are on the last slide, allow user to scroll the page down.
      if (!earlyReturn && direction === 'vertical') {
        const isLastSlide =
          carousel?.track?.details?.abs === carousel?.track?.details?.maxIdx

        if (isLastSlide && event?.deltaY > 0) {
          supportWheel = false
          earlyReturn = true
        }
      }

      /* Prevent default scroll functionality for carousels supporting wheel and scrolling on the correct plane */
      if (supportWheel) {
        event.preventDefault()
      }

      if (earlyReturn) {
        if (touchTimeout && wheelActive) {
          clearTimeout(touchTimeout)
          wheelActive = false
          wheelEnd()
        }
        return
      }

      if (!wheelActive) {
        wheelStart(event)
        wheelActive = true
      }
      wheel(event)
      clearTimeout(touchTimeout)
      touchTimeout = setTimeout(() => {
        wheelActive = false
        wheelEnd()
      }, dragEndDelay)
    }

    carousel.on('created', () => {
      carousel.container.addEventListener('wheel', eventWheel, {
        passive: false,
      })
    })

    carousel.on('destroyed', () => {
      carousel.container.removeEventListener('wheel', eventWheel, {
        passive: false,
      })
      clearTimeout(touchTimeout)
    })
  }
}

/** @param {HTMLButtonElement[]} dots */
const useReveal = (slides = []) => {
  /** @param {import('keen-slider').KeenSliderInstance} carousel */
  return carousel => {
    carousel.on('created', () => {
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.5,
      }

      const observer = new IntersectionObserver(reveal, options)
      observer.observe(carousel.container)
    })

    function reveal (entries, observer) {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          carousel.animator.start([
            { distance: 0.1, duration: 1000, easing: t => easeInOut(t) },
            { distance: -0.1, duration: 1000, easing: t => easeInOut(t) },
          ])
          observer.unobserve(entry.target)
        }
      })
    }

    function easeInOut (t) {
      return -(Math.cos(Math.PI * t) - 1) / 2
    }
  }
}

window.BAO.Carousel = window.BAO.Carousel || {}
window.BAO.Carousel.plugins = {
  setInitialised,
  useArrows,
  useAutoplay,
  useDots,
  useResetIdxOnResize,
  addWheelControls,
  useReveal,
}
